import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { ChevronDown } from 'lucide-react'

import { BackedByLineChartDetailedProps } from '../types'
import { formatChartValue } from '../utils'
import { CategoryTitle } from './CategoryTitle'
import { formatPercentage } from '@/domain/common/format'
import { Collapsible, CollapsibleContent } from '@/ui/atoms/collapsible/Collapsible'
import { cn } from '@/ui/utils/style'

export function BackedByLineChartDetailed({
  items,
  setHighlightedKey,
  underlyingToken,
  handleDetailsExpandedChange,
  getLineChartItemData,
  getDetailItemData,
  className,
}: BackedByLineChartDetailedProps) {
  const totalAmount = items.reduce((acc, item) => acc.plus(item.amount), NormalizedNumber.ZERO)
  if (totalAmount.eq(NormalizedNumber.ZERO)) {
    return null
  }

  return (
    <div className={cn('mb-[5px] w-full px-1 sm:px-0', className)}>
      {items.map((item, index) => {
        const { percentage, color, isHighlighted, isExpandable, isExpanded } = getLineChartItemData(item, index)

        const rowContent = (
          <div className="grid grid-cols-[1fr_auto_auto] items-baseline gap-1.5">
            <div className="flex items-center gap-1.5">
              <div className="flex w-3 items-center justify-center">
                {isExpandable ? (
                  <ChevronDown
                    className={cn('icon-size-xxs transition-transform', isExpanded && 'rotate-180')}
                    style={{ color }}
                  />
                ) : (
                  <div
                    className={cn('size-1.5 rounded-full transition-transform')}
                    style={{ backgroundColor: color }}
                  />
                )}
              </div>
              <div className={cn('typography-label-3 transition-colors', isHighlighted && 'text-secondary')}>
                {item.label}
              </div>
            </div>
            <div className={cn('typography-label-3 text-right transition-colors', isHighlighted && 'text-secondary')}>
              {formatPercentage(percentage)}
            </div>
            <div
              className={cn(
                'typography-body-4 text-right text-secondary transition-colors',
                isHighlighted && 'text-primary',
              )}
            >
              ({formatChartValue(item.amount, underlyingToken)})
            </div>
          </div>
        )
        return (
          <div
            key={item.category}
            className={cn(
              'flex flex-col gap-2 py-2 first:pt-0 last:pb-0',
              (isExpandable || isHighlighted) && 'cursor-pointer',
            )}
            onMouseEnter={() => setHighlightedKey(item.category)}
            onMouseLeave={() => setHighlightedKey(undefined)}
          >
            {isExpandable ? (
              <button
                type="button"
                className={cn(
                  'w-full border-0 bg-transparent p-0 text-left',
                  'focus-visible:rounded-[1px] focus-visible:outline-hidden focus-visible:ring-3',
                  'focus-visible:ring-primary-200 focus-visible:ring-offset-0',
                )}
                aria-expanded={isExpanded}
                onClick={() => {
                  handleDetailsExpandedChange(!isExpanded)
                }}
              >
                {rowContent}
              </button>
            ) : (
              rowContent
            )}
            <div className="relative h-px w-full overflow-hidden rounded-full bg-tertiary">
              <div
                className="absolute top-0 left-0 h-full w-full rounded-full"
                style={{ width: `${percentage.toNumber() * 100}%`, backgroundColor: color }}
              />
            </div>
            {isExpandable && item.amount.gt(NormalizedNumber.ZERO) && item.details.length > 0 && (
              <Collapsible open={isExpanded}>
                <CollapsibleContent className="mt-1 pl-4">
                  <div className="flex flex-col gap-1.5">
                    {item.details.map((detail, detailIndex) => {
                      const {
                        percentage: detailPercentage,
                        color: detailBarColor,
                        isHighlighted: detailHighlighted,
                      } = getDetailItemData(detail, detailIndex, item.amount)
                      return (
                        <div
                          key={detail.category}
                          className={cn('flex flex-col gap-1', detailHighlighted && 'cursor-pointer')}
                          onMouseEnter={() => setHighlightedKey(detail.category)}
                          onMouseLeave={() => setHighlightedKey(item.category)}
                        >
                          <div className="grid grid-cols-[1fr_auto_auto] items-baseline gap-1.5">
                            <div className="flex items-center gap-1.5">
                              <div className="flex w-3 items-center justify-center">
                                <div className="size-1.5 rounded-full" style={{ backgroundColor: detailBarColor }} />
                              </div>
                              <div
                                className={cn(
                                  'typography-body-4 text-secondary transition-colors',
                                  detailHighlighted && 'text-primary',
                                )}
                              >
                                <CategoryTitle category={detail.category} />
                              </div>
                            </div>
                            <div
                              className={cn(
                                'typography-body-4 text-right text-secondary transition-colors',
                                detailHighlighted && 'text-primary',
                              )}
                            >
                              {formatPercentage(detailPercentage)}
                            </div>
                            <div
                              className={cn(
                                'typography-body-4 text-right text-secondary transition-colors',
                                detailHighlighted && 'text-primary',
                              )}
                            >
                              ({formatChartValue(detail.amount, underlyingToken)})
                            </div>
                          </div>
                          <div className="relative h-px w-full overflow-hidden rounded-full bg-tertiary">
                            <div
                              className="absolute top-0 left-0 h-full w-full rounded-full"
                              style={{
                                width: `${detailPercentage.toNumber() * 100}%`,
                                backgroundColor: detailBarColor,
                              }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        )
      })}
    </div>
  )
}
